"use client";

import React, { createContext, useContext, useMemo, ReactNode } from "react";
import { useSession } from "@/lib/auth-client";
import { PERMISSIONS } from "@/permissions";
type PermissionType = (typeof PERMISSIONS)[keyof typeof PERMISSIONS];

export type PermissionContextValue = {
  permissions: PermissionType[];
  isLoading: boolean;
  hasPermission: (permission: PermissionType) => boolean;
  hasAnyPermission: (permissions: PermissionType[]) => boolean;
  hasAllPermissions: (permissions: PermissionType[]) => boolean;
};

const PermissionContext = createContext<PermissionContextValue>(
  {} as PermissionContextValue
);

export function PermissionProvider({ children }: { children: ReactNode }) {
  const { data: session, isPending } = useSession();
  const permissions = useMemo(
    () =>
      (session as { permissions?: PermissionType[] } | null)?.permissions ?? [],
    [session]
  );

  const value = useMemo<PermissionContextValue>(() => {
    const permissionSet = new Set(permissions);

    return {
      permissions,
      isLoading: isPending,
      hasPermission: (permission: PermissionType) =>
        permissionSet?.has(permission) ?? false,
      hasAnyPermission: (required: PermissionType[]) =>
        required.some(p => permissionSet?.has(p) ?? false),
      hasAllPermissions: (required: PermissionType[]) =>
        required.every(p => permissionSet?.has(p) ?? false),
    };
  }, [permissions, isPending]);

  return (
    <PermissionContext.Provider value={value}>
      {children}
    </PermissionContext.Provider>
  );
}

export function usePermissionContext() {
  const context = useContext(PermissionContext);
  if (context === undefined) {
    throw new Error(
      "usePermissionContext must be used within a PermissionProvider"
    );
  }
  return context;
}
